import React, { ReactNode } from 'react';
import {
  FinanceProgramRateType,
  FinanceProgramType,
  PaymentPeriodTiming,
  SubsidyCapType,
} from '@trnsact/trnsact-shared-types';
import { FormSummaryInfo } from '../FormSummaryInfo';
import { getLabelFromEnum } from '../../../../../lib';
import { FinanceProgramForm } from '../../../../../schema';
import { MarkupMethods, Structures } from '../../../../../types';
import { formatCurrency } from 'utils';

interface Props {
  financeProgramFormValues: FinanceProgramForm['program'];
}

export const ConfigureStructureSummaryPart = ({ financeProgramFormValues: program }: Props) => {
  let renderConfiguration: Record<string, ReactNode> = {
    'Rate Type': getLabelFromEnum(FinanceProgramRateType, program.second.rateType),
    'Buy Rate (%)': program.second.rate,
  };

  if (program.second.method === MarkupMethods.CustomerRate) {
    renderConfiguration = {
      ...renderConfiguration,
      'Customer Rate (%)': program.second.customerRate,
    };
  } else if (program.second.structure === Structures.Subsidy) {
    renderConfiguration = {
      ...renderConfiguration,
      'Subsidized Rate': program.second.customerRate,
    };
  }

  renderConfiguration = {
    ...renderConfiguration,
    Method:
      program.second.structure === Structures.Subsidy
        ? 'Subsidy'
        : getLabelFromEnum(MarkupMethods, program.second.method),
    'Program Type': getLabelFromEnum(FinanceProgramType, program.second.programType),
    'Payment Calculation Method': getLabelFromEnum(PaymentPeriodTiming, program.second.paymentCalculationMethod),
    'Terms in Month': program.second.terms?.length ? program.second.terms?.map(term => term)?.join(', ') : null,
    Structure: getLabelFromEnum(Structures, program.second.structure),
  };

  if (program.second.structure === Structures.Markup) {
    renderConfiguration = {
      ...renderConfiguration,
      'Dealer Markup (%)': program.second.dealerMarkup,
    };
  }

  renderConfiguration = {
    ...renderConfiguration,
    'Advance Payment (# of)': program.second.advancePayment,
    'Days To First Payment': program.second.daysToFirstPayment,
    ...Object.fromEntries(
      (program.second.specifySubsidyRates ?? []).flatMap(({ term, subsidyPercent, dnetPercent }) => [
        [`Subsidy % (${term}m)`, subsidyPercent],
        [`DNET % (${term}m)`, dnetPercent],
      ])
    ),
  };

  if (
    program.second.structure === Structures.Subsidy &&
    program.second.configureSubsidyCap &&
    program.second.subsidyCapConfiguration
  ) {
    renderConfiguration['Subsidy Cap Type'] = getLabelFromEnum(
      SubsidyCapType,
      program.second.subsidyCapConfiguration.subsidyCapType ?? ''
    );
    if (program.second.subsidyCapConfiguration.subsidyCapType === SubsidyCapType.FixedAmount) {
      renderConfiguration['Max Subsidy Amount'] = formatCurrency(
        program.second.subsidyCapConfiguration.maxSubsidyAmount
      );
    } else if (program.second.subsidyCapConfiguration.subsidyCapType === SubsidyCapType.NetInvoicePercentage) {
      renderConfiguration['Net Invoice Percentage'] = program.second.subsidyCapConfiguration.netInvoicePercentage;
    }
    renderConfiguration['Lender Name'] = program.second.subsidyCapConfiguration.lenderName;
  }

  return <FormSummaryInfo data={renderConfiguration} />;
};
